import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiMoreVertical, FiSearch } from "react-icons/fi";
import { FaShareAlt } from "react-icons/fa";
import { IoBookmarkOutline } from "react-icons/io5";
import axios from "axios";
import Sidebar from "./Sidebar";
import Header from "./Header";
import Footer from "./Footer";
import NavButtons from "./NavButtons";
import Loader from "./Loader";
import SwipeContainer from "../utils/SwipeContainer";
import {
  ensureValidToken,
  isOfflineMode,
  OFFLINE_CACHE_KEYS,
  cacheData,
  getCachedData,
} from "../utils/authService";
import "../styles/Homemycards.css";

const Homewebholder = () => {
  const [menuOpen, setMenuOpen] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestedCards, setRequestedCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [offline, setOffline] = useState(isOfflineMode());
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const getErrorMessage = useCallback((error) => {
    if (error.response) {
      if (error.response.data) {
        if (typeof error.response.data === 'object') {
          if (error.response.data.detail) {
            return error.response.data.detail;
          }
          if (error.response.data.non_field_errors) {
            return error.response.data.non_field_errors.join(' ');
          }
          const fieldErrors = Object.entries(error.response.data)
            .map(([field, messages]) => `${field}: ${messages.join(' ')}`);
          return fieldErrors.join('\n');
        }
        return error.response.data;
      }
      return `Request failed with status ${error.response.status}`;
    } else if (error.request) {
      return "Network error. Please check your connection.";
    }
    return error.message || "An unexpected error occurred";
  }, []);

  useEffect(() => {
    const savedRequests = JSON.parse(localStorage.getItem('requestedCards') || '[]');
    setRequestedCards(savedRequests);
  }, []);

  console.log(requestedCards);

  const fetchCards = useCallback(async () => {
    // Offline mode: use cached data only, no API calls
    if (isOfflineMode()) {
      const cached = getCachedData(OFFLINE_CACHE_KEYS.webHolder, []);
      setCards(cached);
      setFilteredCards(cached);
      setOffline(true);
      setLoading(false);
      return;
    }

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      const response = await axios.get("https://api.nexcard.co.in/api/cards/", {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      
      // Filter out private cards (where can_access is false)
      const publicCards = response.data.results.filter(card => card.can_access);

      setCards(publicCards);
      setFilteredCards(publicCards);
      cacheData(OFFLINE_CACHE_KEYS.webHolder, publicCards);
      setOffline(false);
      
    } catch (error) {
      console.error("Error fetching cards:", error);
      alert(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [getErrorMessage, navigate]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCards(cards);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = cards.filter(card => {
        return (
          card.full_name?.toLowerCase().includes(query) ||
          card.company_name?.toLowerCase().includes(query) ||
          card.designation?.toLowerCase().includes(query)
        );
      });
      setFilteredCards(filtered);
    }
  }, [searchQuery, cards]);

  const handleCardClick = async (cardId) => {
    navigate(`/card-details/${cardId}`);
  };

  const handleSaveToHolder = async (cardId, e) => {
    e.stopPropagation();

    if (isOfflineMode()) {
      alert("You are offline. Saving cards to My Holder is disabled. Showing last saved data.");
      return;
    }

    setSaving(true);
    
    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      await axios.post(
        "https://api.nexcard.co.in/api/cards/my-holder/",
        { card: cardId },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      alert("Card saved to My Holder successfully!");
    } catch (error) {
      console.error("Error saving card:", error);
      alert(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const isPdfFile = (url) => {
    return url?.endsWith('.pdf') || url?.includes('.pdf?');
  };

  const handleShare = (cardId, e) => {
    e.stopPropagation();
    
    // Find the card to get the owner's name
    const card = cards.find(c => c.id === cardId);
    const ownerName = card?.full_name || 'Someone';
    
    const shareUrl = new URL(`${window.location.origin}/shared-card/${cardId}`);
    shareUrl.searchParams.set('from', 'holder');
    
    const shareText = `Hey! 👋\nHere's the digital business card of *${ownerName}* —\n🔗 Click the link to Open:`;
    
    if (navigator.share) {
      navigator.share({
        title: `${ownerName}'s Business Card`,
        text: shareText,
        url: shareUrl.toString()
      });
    } else {
      // For desktop and fallback, include URL in the clipboard text
      const clipboardText = `${shareText} ${shareUrl.toString()}`;
      navigator.clipboard.writeText(clipboardText);
      alert("Message with link copied to clipboard!");
    }
  };

  const toggleMenu = (index) => {
    setMenuOpen(menuOpen === index ? null : index);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderCard = (card, index) => {
    return (
      <div
        key={card.id}
        className="card"
        onClick={() => handleCardClick(card.id)}
      >
        <div className="card-header">
          <div className="profile">
            {card.profile_image ? (
              <img 
                src={card.profile_image} 
                alt="Profile" 
                onError={(e) => {
                  e.target.src = "https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png";
                }}
              />
            ) : (
              <img
                src="https://www.pngitem.com/pimgs/m/78-786293_1240-x-1240-0-avatar-profile-icon-png.png"
                alt="Default Profile"
              />
            )}
          </div>
          <button
            className="menu-btn"
            onClick={(e) => {
              e.stopPropagation();
              toggleMenu(index);
            }}
          >
            <FiMoreVertical size={20} />
          </button>
        </div>
    
        <div className="card-image-container">
          {isPdfFile(card.file?.file) ? (
            <div className="pdf-preview">
              <div className="pdf-icon">PDF</div>
              <span>{card.file?.name || 'Business Card'}</span>
            </div>
          ) : (
            <img
              src={card.file?.file}
              alt="Business Card"
              className="card-image"
            />
          )}
        </div>
    
        {menuOpen === index && (
          <div className="dropdown-menu" ref={menuRef}>
            <button 
              className="menu-item"
              onClick={(e) => handleShare(card.id, e)}
            >
              <FaShareAlt /> Share
            </button>
            <button 
              className="menu-item"
              onClick={(e) => handleSaveToHolder(card.id, e)}
              disabled={saving}
            >
              <IoBookmarkOutline /> 
              {saving ? "Saving..." : "Save to My Holder"}
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <Header toggleSidebar={toggleSidebar} />
      <NavButtons />

      {showRequestModal && (
        <div className="modal-overlay1" onClick={() => setShowRequestModal(false)}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
            <h3>Request Sent</h3>
            <p>Your request to view this private card has been sent successfully.</p>
            <p>You can view the card when the owner approves your request.</p>
            <button 
              className="modal-close-btn1"
              onClick={() => setShowRequestModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <SwipeContainer>
        <div className="main-content">
          {location.pathname === "/home-web-holder" && (
            <>
              {offline && (
                <div className="offline-banner">
                  You are in offline mode. Showing last saved web holder cards.
                </div>
              )}
              <div className="search-container">
                <div className="search-bar">
                  <FiSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Search Name/ Company/ Designation..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  className="add-card-btn"
                  onClick={() => {
                    if (isOfflineMode()) {
                      alert("You are offline. Creating new cards is disabled in offline mode.");
                      return;
                    }
                    navigate("/add-new-card");
                  }}
                  disabled={isOfflineMode()}
                >
                  Add New Card
                </button>
              </div>

              <div className="cards-list">
                {filteredCards.map((card, index) => renderCard(card, index))}
              </div>
            </>
          )}
        </div>
      </SwipeContainer>

      <Footer />
    </>
  );
};

export default Homewebholder;